import type { DiaryStatus, UserRole } from '@/types/models';

/**
 * Why a record cannot be changed, said in words, before anyone types.
 *
 * The database is the authority (RLS and the 00xx triggers refuse what a
 * role may not do). These helpers only mirror those rules on the screen, so
 * a person sees "read only, and this is why" when they open the record
 * instead of a form that looks editable and then does not save. Keep each
 * rule in step with the policy or trigger named beside it.
 *
 * Every helper returns `null` when the record may be changed, otherwise the
 * sentence to show.
 */

const ROLE_LABEL: Record<UserRole, string> = {
  operator: 'operator',
  supervisor: 'supervisor',
  maintenance: 'maintenance',
  manager: 'manager',
  admin: 'administrator',
};

const WHO: Record<string, string> = {
  'supervisor,manager,admin': 'a supervisor, manager or administrator',
  'maintenance,supervisor,manager,admin': 'the workshop, a supervisor, manager or administrator',
  'maintenance,manager,admin': 'the workshop, a manager or an administrator',
  'manager,admin': 'a manager or an administrator',
  admin: 'an administrator',
};

/** "Read only for your role" with who can change it instead. */
export function roleLock(
  role: UserRole | null | undefined,
  allowed: readonly UserRole[],
  what: string,
): string | null {
  if (role && allowed.includes(role)) return null;
  const who = WHO[allowed.join(',')] ?? allowed.map((r) => ROLE_LABEL[r]).join(', ');
  const as = role ? `As ${article(ROLE_LABEL[role])} ${ROLE_LABEL[role]} you` : 'You';
  return `${as} can view ${what} but not change them. Ask ${who} to make the change.`;
}

/**
 * The diary rules of p_diary_update (0028) and upsert_diary (0046):
 *   - approved: locked for everyone; a supervisor re-opens it first;
 *   - a supervisor, manager or administrator may change anything else;
 *   - anyone else only their own diary, and only while it is a draft.
 */
export function diaryLock(opts: {
  status: DiaryStatus;
  /** Is the diary already saved (on this device or the server)? */
  saved: boolean;
  createdBy: string | null | undefined;
  userId: string | null | undefined;
  isLead: boolean;
}): string | null {
  const { status, saved, createdBy, userId, isLead } = opts;
  if (status === 'approved') {
    return isLead
      ? 'Approved — locked. Re-open it to make a change; it then has to be submitted and approved again.'
      : 'Approved — locked. Only a supervisor can re-open it.';
  }
  if (!saved || isLead) return null;
  if (createdBy && userId && createdBy !== userId) {
    return 'This diary was captured by someone else. Only its author (while it is a draft) or a supervisor can change it.';
  }
  if (status !== 'draft') {
    return `This diary is ${status}. Only a supervisor can change it now — ask one to make the correction.`;
  }
  return null;
}

function article(word: string): string {
  return /^[aeiou]/i.test(word) ? 'an' : 'a';
}
